import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Shield, RefreshCw, Home, LogOut, Router, Printer, Flame } from 'lucide-react';
import { toast } from 'sonner';

const MODULE_META = {
  cnc_routing:   { label: 'CNC',  icon: Router,  granted: 'bg-[#c85a00] text-white', revoked: 'bg-zinc-800 text-zinc-400 border border-zinc-700' },
  '3d_printing': { label: '3DP',  icon: Printer, granted: 'bg-[#c85a00] text-white', revoked: 'bg-zinc-800 text-zinc-400 border border-zinc-700' },
  laser_cutting: { label: 'Laser', icon: Flame,  granted: 'bg-[#c85a00] text-white', revoked: 'bg-zinc-800 text-zinc-400 border border-zinc-700' },
};

const MODULES = ['cnc_routing', '3d_printing', 'laser_cutting'];

const MODULE_DISPLAY = { cnc_routing: 'CNC Router', '3d_printing': '3D Printing', laser_cutting: 'Laser Cutting' };

export default function UserManagement() {
  const { token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState(null); // { userId, email, module, action: 'grant'|'revoke' }

  const headers = { Authorization: `Bearer ${token}` };

  const loadUsers = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/users-list`, { headers });
      setUsers(res.data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executeModuleAction = async () => {
    if (!confirmAction) return;
    const { userId, module, action } = confirmAction;
    const endpoint = action === 'grant' ? 'grant-module' : 'revoke-module';
    try {
      await axios.patch(`${API}/admin/users/${userId}/${endpoint}`, { module }, { headers });
      toast.success(`${MODULE_DISPLAY[module]} access ${action === 'grant' ? 'granted' : 'revoked'}`);
      await loadUsers();
    } catch {
      toast.error('Failed to update module access');
    } finally {
      setConfirmAction(null);
    }
  };

  const handleModuleClick = (user, module) => {
    const disciplineAccess = user.discipline_access || {};
    const hasAccess = disciplineAccess[module];
    setConfirmAction({
      userId: user.id,
      email: user.email,
      module,
      action: hasAccess ? 'revoke' : 'grant'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-orange-500" />
          <h1 className="text-2xl font-bold text-white">User Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <Home className="w-4 h-4 mr-2" />
            Admin Home
          </Button>
          <Button onClick={loadUsers} disabled={refreshing} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="ghost" onClick={logout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs text-zinc-400">
        <span className="font-semibold text-zinc-300">Module toggles:</span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-[#c85a00]" /> Granted
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-zinc-700" /> Not granted — click to grant
        </span>
        <span className="text-zinc-500">Super admins always have full access.</span>
      </div>

      {/* Users table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-lg">{users.length} Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-400 font-medium px-4 py-3">Name</th>
                  <th className="text-left text-zinc-400 font-medium px-4 py-3">Email</th>
                  <th className="text-left text-zinc-400 font-medium px-4 py-3">Module Access</th>
                  <th className="text-left text-zinc-400 font-medium px-4 py-3">Role</th>
                  <th className="text-left text-zinc-400 font-medium px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const disciplineAccess = u.discipline_access || {};
                  const isSuperAdmin = u.is_super_admin || false;
                  return (
                    <tr key={u.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                      <td className="px-4 py-3 text-white font-medium">{u.full_name || '—'}</td>
                      <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 flex-wrap">
                          {MODULES.map(mod => {
                            const meta = MODULE_META[mod];
                            const hasAccess = isSuperAdmin || disciplineAccess[mod];
                            return (
                              <button
                                key={mod}
                                disabled={isSuperAdmin}
                                onClick={() => !isSuperAdmin && handleModuleClick(u, mod)}
                                title={isSuperAdmin ? 'Super admins always have full access' : (hasAccess ? `Revoke ${MODULE_DISPLAY[mod]}` : `Grant ${MODULE_DISPLAY[mod]}`)}
                                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${hasAccess ? meta.granted : meta.revoked} ${isSuperAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                              >
                                {meta.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {isSuperAdmin && <Badge className="bg-orange-600 text-white text-xs">Super Admin</Badge>}
                        {!isSuperAdmin && u.is_admin && <Badge className="bg-blue-600 text-white text-xs">Admin</Badge>}
                        {!isSuperAdmin && !u.is_admin && <span className="text-zinc-500 text-xs">Tester</span>}
                      </td>
                      <td className="px-4 py-3 text-zinc-500 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <AlertDialog open={Boolean(confirmAction)} onOpenChange={open => !open && setConfirmAction(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirmAction?.action === 'grant' ? 'Grant' : 'Revoke'} {confirmAction && MODULE_DISPLAY[confirmAction.module]} Access
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmAction?.action === 'grant'
                ? `Grant ${confirmAction && MODULE_DISPLAY[confirmAction.module]} access to ${confirmAction?.email}? They will be able to log in with this module.`
                : `Revoke ${confirmAction && MODULE_DISPLAY[confirmAction.module]} access from ${confirmAction?.email}? They will no longer be able to log in with this module.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeModuleAction}
              className={confirmAction?.action === 'grant' ? 'bg-[#c85a00] hover:bg-[#a84800] text-white' : 'bg-red-700 hover:bg-red-600 text-white'}
            >
              {confirmAction?.action === 'grant' ? 'Grant Access' : 'Revoke Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
